import React, { useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';

import i18n from 'i18n-js';
import {
  getSecureValue,
  isAndroid,
  isWeb,
  languages,
  moderateScale,
  navigateTo,
  Popup,
} from '../../../helpers';
import { scale } from '@components/frame';

import TablerWrapper from '@components/auntification/tablerWrapper';
import {
  AUTH_LOGO_MARGIN_TOP_SMALL,
  AUTH_LOGO_MAX_WIDTH,
  AUTH_LOGO_OFFSET_FORM,
  AUTH_OFFSET_FORM_LARGE,
} from '../tablerWrapper/constants';
import Button from '@components/button/index';
import Logo from '@components/logo/index';

import { logOnboarding } from '@reducers/onBoarding';
import TouchableElement from '@components/common/touchableElement';
import ColorsWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import ChangeFactoryLanguage from './modals/changeFactoryLanguage';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';

const ValueLine = ({ C, value, onClick }) => {
  return (
    <TouchableElement
      onPress={() => {
        onClick();
      }}
    >
      {({ pressHoverColor, boxStyle }) => (
        <View
          style={[
            {
              paddingHorizontal: scale(15),
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'row',
              height: scale(41.5) - (isAndroid ? StyleSheet.hairlineWidth : 0),
              borderRadius: scale(10),
              backgroundColor: C(pressHoverColor(modalFieldBg)),
            },
            boxStyle,
          ]}
        >
          <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1 }]}>
            <View>
              <Text
                style={[
                  {
                    color: C(pressHoverColor('Label/Light/Primary')),
                    fontVariant: ['tabular-nums'],
                  },
                  font.Body,
                ]}
              >
                {i18n.t(value ? value : 'Select Language')}
              </Text>
            </View>
            <View>
              <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
            </View>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

const CreateAccount = (props) => {
  const { route, C, logOnboarding, onBoarding } = props;
  const scenario = route.params?.scenario;
  const data = route?.params?.data;

  const [language, setLanguage] = useState(() => {
    // First try to get from storage
    const saved = getSecureValue('systemLanguage');
    if (saved && saved !== 'false') {
      return saved;
    }

    // Fallback: detect browser language directly
    if (typeof navigator !== 'undefined' && navigator.language) {
      const navLang = navigator.language.toLowerCase();

      // Try exact match first
      const exactMatch = languages.find((lng) => lng.value.toLowerCase() === navLang);
      if (exactMatch) {
        return exactMatch.value;
      }

      // Try partial match (e.g., 'en-US' matches 'en')
      const partialMatch = languages.find(
        (lng) => navLang.startsWith(lng.value.toLowerCase()) || lng.value.toLowerCase().startsWith(navLang.split('-')[0])
      );
      if (partialMatch) {
        return partialMatch.value;
      }
    }

    // No match found - return null to show "Select Language"
    return null;
  });

  const submit = async () => {
    const props = { ...data, language };
    switch (scenario) {
      case 'onBoarding': {
        logOnboarding(`Component: Enter factory language. Action: Next`, { ...props, component: 'Enter factory language', action: 'next' });
        navigateTo('CreatePassword', { data: props, scenario: 'onBoarding' });
        return;
      }
    }
  };

  const toggleFactoryLanguage = () => {
    Popup({
      Component: (p) => <ChangeFactoryLanguage {...{ ...p, selectedValue: language, change: setLanguage }} />,
    });
  };

  return (
    <TablerWrapper offset={AUTH_OFFSET_FORM_LARGE} keyboardAvoid>
      <Logo
        title={i18n.t('Factory Language')}
        small
        marginTop={AUTH_LOGO_MARGIN_TOP_SMALL}
        maxWidth={AUTH_LOGO_MAX_WIDTH}
        offset={AUTH_LOGO_OFFSET_FORM}
        isBack
      />
      <View style={styles.inputContainer}>
        <View>
          <View style={{ paddingLeft: scale(15), marginTop: scale(26), marginBottom: scale(5) }}>
            <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('FACTORY LANGUAGE')}</Text>
          </View>
          <View>
            <ValueLine
              {...{
                C,
                value: languages.find((i) => i.value === language)?.name || '',
                onClick: toggleFactoryLanguage,
              }}
            />
          </View>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(6) }}>
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
              {i18n.t('Invites to colleagues will be sent in this language.')}
            </Text>
          </View>
        </View>
        <Button
          {...{
            name: i18n.t('Next'),
            disabled: !language,
            onPress: submit,
          }}
        />
      </View>
    </TablerWrapper>
  );
};

export default connect(({ onBoarding }) => ({ onBoarding }), { logOnboarding })(ColorsWrapper(CreateAccount));

const INPUT_CONTAINER_HEIGHT = 334;
const INPUT_CONTAINER_TOP_MARGIN = 21;
const INPUT_CONTAINER_PHONE_WIDTH = '90%';

const styles = StyleSheet.create({
  inputContainer: {
    width: isWeb ? '100%' : INPUT_CONTAINER_PHONE_WIDTH,
    justifyContent: 'space-between',
    height: moderateScale(INPUT_CONTAINER_HEIGHT),
    marginTop: moderateScale(INPUT_CONTAINER_TOP_MARGIN),
  },
});
